import React, { useEffect, useState, useCallback } from 'react';
import {
  Container, Typography, Paper, Box, Alert, CircularProgress, Button,
  Grid, Card, CardContent, LinearProgress, Switch, FormControlLabel, Divider, Chip
} from '@mui/material';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import ImageIcon from '@mui/icons-material/Image';
import FolderDeleteIcon from '@mui/icons-material/FolderDelete';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SdStorageIcon from '@mui/icons-material/SdStorage';
import RefreshIcon from '@mui/icons-material/Refresh';
import { swarmApi } from '../api';

function formatBytes(b) {
  if (!b || b === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(b) / Math.log(1024));
  return `${(b / Math.pow(1024, i)).toFixed(1)} ${units[i] || 'B'}`;
}

function Cleanup() {
  const [diskUsage, setDiskUsage] = useState(null);
  const [containers, setContainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [lastResult, setLastResult] = useState(null);
  const [autoPrune, setAutoPrune] = useState(() => {
    try { return JSON.parse(localStorage.getItem('autoPrune') || 'false'); } catch { return false; }
  });

  const user = (() => {
    try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; }
  })();
  const isAdmin = user?.role === 'admin';

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [diskRes, containerRes] = await Promise.all([
        swarmApi.getDiskUsage().catch(() => ({ data: null })),
        swarmApi.listContainers(true).catch(() => ({ data: [] })),
      ]);
      setDiskUsage(diskRes.data);
      setContainers(containerRes.data);
      setError('');
    } catch {
      setError('Failed to fetch cleanup data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-prune: run container prune every 24h if enabled
  useEffect(() => {
    if (!autoPrune || !isAdmin) return;
    const interval = setInterval(async () => {
      try {
        await swarmApi.pruneContainers();
        await swarmApi.pruneImages();
      } catch { /* ignore auto-prune failures */ }
    }, 24 * 60 * 60 * 1000); // 24 hours
    return () => clearInterval(interval);
  }, [autoPrune, isAdmin]);

  const handleAutoPruneToggle = (e) => {
    const val = e.target.checked;
    setAutoPrune(val);
    localStorage.setItem('autoPrune', JSON.stringify(val));
  };

  const handlePruneContainers = async () => {
    const deadCount = containers.filter(c => c.state !== 'running').length;
    if (deadCount === 0) { setLastResult({ severity: 'info', message: 'No stopped containers to remove.' }); return; }
    if (!window.confirm(`Remove all ${deadCount} non-running containers? This cannot be undone.`)) return;
    setActionLoading('containers');
    try {
      const res = await swarmApi.pruneContainers();
      const mb = formatBytes(res.data.spaceReclaimed || 0);
      const ghosts = res.data.ghostContainers || 0;
      const removed = res.data.removed || 0;
      if (ghosts > 0) {
        setLastResult({
          severity: removed > 0 ? 'warning' : 'warning',
          message: `Removed ${removed} container${removed !== 1 ? 's' : ''}, reclaimed ${mb}. ${ghosts} ghost container${ghosts !== 1 ? 's' : ''} could not be removed — these require a Docker daemon restart on the server to clear.`
        });
      } else {
        setLastResult({ severity: 'success', message: `Pruned ${removed} containers, reclaimed ${mb}` });
      }
      await fetchData();
    } catch {
      setLastResult({ severity: 'error', message: 'Container prune failed' });
    }
    setActionLoading(null);
  };

  const handlePruneImages = async () => {
    if (!window.confirm('Remove all unused (dangling) Docker images? This cannot be undone.')) return;
    setActionLoading('images');
    try {
      const res = await swarmApi.pruneImages();
      const mb = formatBytes(res.data.spaceReclaimed || 0);
      setLastResult({ severity: 'success', message: `Pruned ${res.data.removed || 0} images, reclaimed ${mb}` });
      await fetchData();
    } catch {
      setLastResult({ severity: 'error', message: 'Image prune failed' });
    }
    setActionLoading(null);
  };

  const handlePruneVolumes = async () => {
    if (!window.confirm('Remove all orphan (unused) volumes? Data in these volumes will be permanently lost!')) return;
    setActionLoading('volumes');
    try {
      const res = await swarmApi.pruneVolumes();
      const mb = formatBytes(res.data.spaceReclaimed || 0);
      setLastResult({ severity: 'success', message: `Pruned ${res.data.removed || 0} volumes, reclaimed ${mb}` });
      await fetchData();
    } catch {
      setLastResult({ severity: 'error', message: 'Volume prune failed' });
    }
    setActionLoading(null);
  };

  const deadContainers = containers.filter(c => c.state !== 'running').length;

  return (
    <Container maxWidth="xl">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CleaningServicesIcon fontSize="large" color="primary" /> Cleanup
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Remove unused Docker resources to free disk space
          </Typography>
        </Box>
        <Button startIcon={<RefreshIcon />} onClick={fetchData} disabled={loading}>Refresh</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {lastResult && (
        <Alert severity={lastResult.severity} sx={{ mb: 2 }} onClose={() => setLastResult(null)}>
          {lastResult.message}
        </Alert>
      )}

      {/* Disk Usage Overview */}
      {diskUsage && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            <SdStorageIcon sx={{ verticalAlign: 'middle', mr: 1 }} fontSize="small" />
            Disk Usage Overview — {formatBytes(diskUsage.totalBytes)}
          </Typography>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={4}>
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2"><strong>Images</strong> ({diskUsage.images?.count})</Typography>
                  <Typography variant="body2" color="text.secondary">{formatBytes(diskUsage.images?.sizeBytes)}</Typography>
                </Box>
                <LinearProgress variant="determinate" value={diskUsage.totalBytes > 0 ? Math.min((diskUsage.images?.sizeBytes || 0) / diskUsage.totalBytes * 100, 100) : 0} color="primary" sx={{ height: 10, borderRadius: 5 }} />
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2"><strong>Containers</strong> ({diskUsage.containers?.count})</Typography>
                  <Typography variant="body2" color="text.secondary">{formatBytes(diskUsage.containers?.sizeBytes)}</Typography>
                </Box>
                <LinearProgress variant="determinate" value={diskUsage.totalBytes > 0 ? Math.min((diskUsage.containers?.sizeBytes || 0) / diskUsage.totalBytes * 100, 100) : 0} color="warning" sx={{ height: 10, borderRadius: 5 }} />
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2"><strong>Volumes</strong> ({diskUsage.volumes?.count})</Typography>
                  <Typography variant="body2" color="text.secondary">{formatBytes(diskUsage.volumes?.sizeBytes)}</Typography>
                </Box>
                <LinearProgress variant="determinate" value={diskUsage.totalBytes > 0 ? Math.min((diskUsage.volumes?.sizeBytes || 0) / diskUsage.totalBytes * 100, 100) : 0} color="success" sx={{ height: 10, borderRadius: 5 }} />
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      {loading && !diskUsage && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      )}

      {/* Prune Actions */}
      {isAdmin && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <ViewInArIcon color="warning" />
                  <Typography variant="h6">Containers</Typography>
                  {deadContainers > 0 && <Chip label={`${deadContainers} dead`} size="small" color="warning" />}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Remove all stopped, exited, and dead containers. Running containers are never affected.
                </Typography>
                <Button
                  variant="contained"
                  color="warning"
                  startIcon={actionLoading === 'containers' ? <CircularProgress size={16} color="inherit" /> : <DeleteSweepIcon />}
                  onClick={handlePruneContainers}
                  disabled={!!actionLoading}
                  fullWidth
                >
                  Prune Containers ({deadContainers})
                </Button>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <ImageIcon color="primary" />
                  <Typography variant="h6">Images</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Remove unused (dangling) images that are no longer referenced by any container or service. This is the biggest space saver after deployments.
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={actionLoading === 'images' ? <CircularProgress size={16} color="inherit" /> : <ImageIcon />}
                  onClick={handlePruneImages}
                  disabled={!!actionLoading}
                  fullWidth
                >
                  Prune Images
                </Button>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <FolderDeleteIcon color="error" />
                  <Typography variant="h6">Volumes</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Remove orphan volumes not used by any service. <strong>Caution:</strong> data in orphan volumes is permanently deleted.
                </Typography>
                <Button
                  variant="contained"
                  color="error"
                  startIcon={actionLoading === 'volumes' ? <CircularProgress size={16} color="inherit" /> : <FolderDeleteIcon />}
                  onClick={handlePruneVolumes}
                  disabled={!!actionLoading}
                  fullWidth
                >
                  Prune Volumes
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Auto-Prune Schedule */}
      {isAdmin && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <ScheduleIcon color="info" />
            <Typography variant="h6">Auto-Prune Schedule</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            When enabled, the dashboard will automatically prune dead containers and unused images every 24 hours while the browser tab is open. This runs client-side — consider setting up a server-side cron job for unattended cleanup.
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <FormControlLabel
            control={<Switch checked={autoPrune} onChange={handleAutoPruneToggle} color="primary" />}
            label={
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Enable auto-prune (containers + images every 24h)
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {autoPrune ? 'Active — will prune next in 24 hours' : 'Disabled — manual cleanup only'}
                </Typography>
              </Box>
            }
          />
        </Paper>
      )}

      {!isAdmin && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Cleanup actions are only available to administrators.
        </Alert>
      )}
    </Container>
  );
}

export default Cleanup;
